import { useState } from 'react';

const STEPS = [
  {
    num: '01',
    icon: 'ri-stethoscope-line',
    title: 'Bắt Mạch Tài Sản',
    desc: 'Phân tích hiện trạng tài sản, xác định vấn đề cốt lõi và tiềm năng chưa được khai thác.',
  },
  {
    num: '02',
    icon: 'ri-map-2-line',
    title: 'Bản Đồ Mô Hình',
    desc: 'Thiết kế mô hình khai thác tối ưu: bán đúng giá, cho thuê đúng phân khúc, vận hành đúng chuẩn.',
  },
  {
    num: '03',
    icon: 'ri-tools-line',
    title: 'Triển Khai',
    desc: 'Thực thi toàn bộ theo bản đồ: sửa chữa, xây dựng, setup nội thất, định vị thị trường.',
  },
  {
    num: '04',
    icon: 'ri-settings-3-line',
    title: 'Vận Hành',
    desc: 'Quản lý cho thuê, bảo trì, tối ưu công suất — chủ nhà không cần tự làm bất cứ điều gì.',
  },
  {
    num: '05',
    icon: 'ri-line-chart-line',
    title: 'Tăng Giá Trị',
    desc: 'Tài sản tăng giá trị thực tế, dòng tiền tăng 2–5 lần, sẵn sàng bán hoặc mở rộng danh mục.',
  },
];

function SolutionStep({ step, index }: { step: typeof STEPS[0]; index: number }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative flex flex-col items-center text-center group/sol cursor-pointer"
      onClick={() => setOpen(v => !v)}
    >
      {/* Circle */}
      <div className={`relative z-10 w-14 h-14 flex items-center justify-center rounded-full mb-5 transition-all duration-300 border-2 ${
        index === 0 || index === 4
          ? 'bg-[#004D4D] border-[#004D4D] group-hover/sol:bg-[#C5A059] group-hover/sol:border-[#C5A059]'
          : 'bg-white border-[#004D4D]/30 group-hover/sol:border-[#C5A059] group-hover/sol:bg-[#C5A059]/10'
      }`}>
        <i className={`${step.icon} text-xl ${
          index === 0 || index === 4 ? 'text-white group-hover/sol:text-[#002626]' : 'text-[#004D4D]'
        } transition-colors`} />
      </div>

      {/* Step number */}
      <span className="text-[#C5A059] text-xs font-heading font-bold tracking-widest mb-1">{step.num}</span>
      <div className="flex items-center gap-1 justify-center">
        <h4 className="font-heading font-bold text-base text-[#004D4D] mb-0">{step.title}</h4>
        <i className={`ri-arrow-down-s-line text-[#004D4D]/30 text-sm transition-transform duration-200 ${open ? 'rotate-180' : ''} md:group-hover/sol:rotate-180`} />
      </div>
      {/* Mobile toggle */}
      <div className={`md:hidden overflow-hidden transition-all duration-300 ${open ? 'max-h-24 opacity-100 mt-2' : 'max-h-0 opacity-0'}`}>
        <p className="text-[#4B5563] text-xs font-sans leading-relaxed">{step.desc}</p>
      </div>
      {/* Desktop hover */}
      <div className="hidden md:block overflow-hidden max-h-0 opacity-0 group-hover/sol:max-h-24 group-hover/sol:opacity-100 group-hover/sol:mt-2 transition-all duration-300">
        <p className="text-[#4B5563] text-xs font-sans leading-relaxed">{step.desc}</p>
      </div>
    </div>
  );
}

export default function SolutionSection() {
  return (
    <section id="solution" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* Header */}
        <div className="max-w-2xl mb-16">
          <span className="text-[#C5A059] text-xs font-heading font-bold tracking-widest uppercase">
            PHƯƠNG PHÁP UPLIVING
          </span>
          <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-[#004D4D] mt-3 mb-5 leading-tight">
            Từ tài sản bỏ phí<br />đến dòng tiền bền vững
          </h2>
          <p className="text-[#374151] text-base font-sans leading-relaxed">
            UPLIVING không phải môi giới, không phải nhà thầu. Chúng tôi là công ty thiết kế mô hình tài sản — từ phân tích đến vận hành, chịu trách nhiệm với kết quả.
          </p>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Connector line - desktop */}
          <div className="hidden lg:block absolute top-12 left-[calc(10%+24px)] right-[calc(10%+24px)] h-px bg-gradient-to-r from-[#004D4D]/20 via-[#C5A059]/40 to-[#004D4D]/20" />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {STEPS.map((step, i) => (
              <SolutionStep key={step.num} step={step} index={i} />
            ))}
          </div>
        </div>

        {/* Bottom claim */}
        <div className="mt-16 bg-[#F2F4F5] rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-12 h-12 flex items-center justify-center bg-[#004D4D] rounded-full flex-shrink-0">
              <i className="ri-shield-check-line text-xl text-white" />
            </div>
            <div>
              <p className="font-heading font-bold text-[#004D4D] text-lg leading-tight">
                "Chúng tôi không bán dịch vụ —
              </p>
              <p className="font-heading font-bold text-[#C5A059] text-lg leading-tight">
                chúng tôi thiết kế kết quả"
              </p>
            </div>
          </div>
          <a
            href="/about"
            className="inline-flex items-center gap-2 bg-[#004D4D] hover:bg-[#0A5F5F] text-white font-heading font-semibold text-sm px-7 py-3.5 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer flex-shrink-0"
          >
            Về UPLIVING
            <i className="ri-arrow-right-line" />
          </a>
        </div>

      </div>
    </section>
  );
}
